import { DownOutlined } from '@ant-design/icons';
import AppDescription from '@nxsys/components/AppDescription';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import {
  defaultPaymentFrequency,
  paymentModelOptionsIreland,
} from '@nxsys/constants/AppConstants';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import {
  defaultsBusinessTypeOptions,
  titleOptions,
} from '@nxsys/constants/prospect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import { Candidate } from '@nxsys/store/slices/prospect/types';
import { Icons } from '@nxsys/types/icons';
import { formatStatus } from '@nxsys/utils';
import { Descriptions } from 'antd';
import { find } from 'lodash';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CandidateForm, {
  CandidateFormRef,
  CandidateFormValues,
} from './CandidateForm';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';

type CreateCandidateProps = {
  isOpen: boolean;
  toggle: () => void;
  data: Candidate | null;
  refetchData: () => void;
  handleProgressOnboardingByIds: (ids: string[]) => void;
  handleProgressRegistrationByIds: (ids: string[]) => void;
  handleDeleteCandidateByIds: (ids: string[]) => void;
};

const ViewCandidateModal: React.FC<CreateCandidateProps> = ({
  isOpen,
  toggle,
  data,
  refetchData,
  handleProgressOnboardingByIds,
  handleProgressRegistrationByIds,
  handleDeleteCandidateByIds,
}: CreateCandidateProps) => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch<AppDispatch>();
  const appStore = useSelector((state: AppState) => state.appStore);
  const { payrollCountry, isBureau } = appStore;
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  const isIreland = payrollCountry === IRELAND;

  // Ireland has its own PAYE/RCT list; RCT is stored as the BE value CIS
  const defaultPaymentModel = isIreland
    ? paymentModelOptionsIreland
    : appStore?.appConfigs?.paymentModels;

  const formRef = useRef<CandidateFormRef>(null);

  const [isEditing, setIsEditing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: CandidateFormValues) => {
    if (!data?.id) return;

    setIsSubmitting(true);
    try {
      const params = {
        DOB: values.dob ? moment(values.dob).toISOString() : null,
        agencyId: values.agency?.value ?? null,
        // Business type only belongs to the self-employed models, and neither
        // bureau nor Ireland has one
        businessType:
          !isBureau &&
          !isIreland &&
          ['CIS', 'NON_CIS'].includes(values.paymentModel?.value as string)
            ? (values.businessType?.value ?? null)
            : null,
        companyPaymentId: values.paymentCompany?.value ?? null,
        email: values.email ?? null,
        forename: values.forename ?? null,
        payFrequency: values.payFrequency ?? null,
        paymentModel: values.paymentModel?.value ?? null,
        phone: values.phoneNumber || null,
        status: 'PENDING',
        surname: values.surname ?? null,
        title: values.title?.value ?? null,
      };
      if (isUK) {
        params.paymentModel = PAYMENT_MODEL_ENUM.PAYEE;
      }
      // unwrap() so a rejected request throws — without it the thunk resolves
      // and the modal closes as if the update had succeeded
      await dispatch(
        prospectActions.updateCandidateById({
          id: data.id,
          payload: params,
        }),
      ).unwrap();
      refetchData();
      setIsEditing(false);
      toggle();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const actionButtonMenuProps = {
    items: [
      {
        key: '1',
        label: messages['common.edit'],
        onClick: () => setIsEditing(true),
      },
      {
        key: '2',
        label: messages['prospect.candidate.progressToRegistration'],
        onClick: () => {
          if (data?.id) {
            handleProgressRegistrationByIds([data.id]);
            toggle();
          }
        },
      },
      {
        key: '3',
        label: messages['prospect.candidate.progressToOnboarding'],
        onClick: () => {
          if (data?.id) {
            handleProgressOnboardingByIds([data.id]);
            toggle();
          }
        },
      },
    ],
  };

  const descriptionItems = [
    {
      label: 'Title',
      value:
        find(titleOptions, (opt) => opt.value === data?.title)?.label ?? '',
    },
    {
      label: 'Forename',
      value: data?.forename ?? '',
    },
    {
      label: 'Surname',
      value: data?.surname ?? '',
    },
    {
      label: 'DOB',
      value: data?.DOB ? moment(data.DOB).format('DD/MM/YYYY') : '',
    },
    {
      label: 'Email',
      value: data?.email ?? '',
    },
    {
      label: 'Phone Number',
      value: data?.phone ?? '',
    },
    {
      label: isBureau
        ? messages['common.employer']
        : messages['common.paymentCompany'],
      value: data?.companyPayment?.companyName ?? '',
    },
    ...(!isBureau
      ? [
          {
            label: messages['common.agencyName'],
            value: data?.agencyName?.name ?? '',
          },
        ]
      : []),
    ...(!isUK
      ? [
          {
            label: messages['common.paymentModel'],
            value:
              find(
                defaultPaymentModel,
                (opt) => opt.value === data?.paymentModel,
              )?.label ?? '',
          },
        ]
      : []),
    {
      label: 'Pay Frequency',
      value:
        find(defaultPaymentFrequency, (opt) => opt.value === data?.payFrequency)
          ?.label ?? '',
    },
    // Ireland has no business type — its CIS value is RCT
    ...(!isBureau &&
    !isIreland &&
    (data?.paymentModel === PAYMENT_MODEL_ENUM.CIS ||
      data?.paymentModel === PAYMENT_MODEL_ENUM.NON_CIS)
      ? [
          {
            label: 'Business Type',
            value:
              find(
                defaultsBusinessTypeOptions,
                (opt) => opt.value === data?.businessType,
              )?.label ?? '',
          },
        ]
      : []),
  ];

  return (
    <AppModalContainer
      title={
        <div className="flex gap-x-4">
          {messages['prospect.candidateDetail']}
          {data?.status && (
            <AppTagStatus
              className={`tag-popup ${formatStatus(data?.status ?? '')}`}
            >
              {formatStatus(data?.status ?? '')}
            </AppTagStatus>
          )}
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      // Unmount on close so unsaved edits are dropped and the next candidate
      // opens with its own values
      destroyOnClose
      actions={
        isEditing
          ? [
              {
                name: messages['common.cancel'],
                variant: 'outlined',
                onClick: () => setIsEditing(false),
              },
              {
                name: messages['common.save'],
                buttonType: 'primary',
                onClick: () => formRef.current?.submit(),
                disabled: isSubmitting,
                loading: isSubmitting,
              },
            ]
          : [
              {
                name: messages['common.delete'],
                variant: 'outlined',
                onClick: () => {
                  if (data?.id) {
                    handleDeleteCandidateByIds([data.id]);
                    toggle();
                  }
                },
                leftIcon: <Icons.IconDelete />,
                className: 'btn-delete',
              },
              {
                name: messages['common.action'],
                buttonType: 'primary',
                onClick: () => {},
                actionType: 'dropdown',
                menu: actionButtonMenuProps,
                rightIcon: <DownOutlined />,
              },
            ]
      }
    >
      {!isEditing ? (
        <AppDescription
          column={1}
          labelStyle={{ width: 216 }}
          bordered={false}
          colon={false}
          size="middle"
        >
          {descriptionItems?.map((item, _index) => (
            <Descriptions.Item key={_index} label={item.label}>
              {item.value ?? '-'}
            </Descriptions.Item>
          ))}
        </AppDescription>
      ) : (
        <CandidateForm
          formRef={formRef}
          onSubmit={handleSubmit}
          defaultValues={{
            title:
              find(titleOptions, (opt) => opt.value === data?.title) ?? null,
            dob: data?.DOB,
            forename: data?.forename ?? '',
            surname: data?.surname ?? '',
            agency: data?.agencyName
              ? {
                  label: data?.agencyName?.name ?? '',
                  value: data?.agencyName?.id ?? '',
                }
              : null,
            paymentCompany: data?.companyPayment
              ? {
                  label: data?.companyPayment?.companyName ?? '',
                  value: data?.companyPayment?.id ?? '',
                  fullData: data?.companyPayment ?? null,
                }
              : null,
            email: data?.email ?? '',
            phoneNumber: data?.phone ?? '',
            payFrequency:
              find(
                defaultPaymentFrequency,
                (opt) => opt.value === data?.payFrequency,
              )?.value ?? '',
            paymentModel:
              find(
                defaultPaymentModel,
                (opt) => opt.value === data?.paymentModel,
              ) ?? null,
            businessType:
              find(
                defaultsBusinessTypeOptions,
                (opt) => opt.value === data?.businessType,
              ) ?? null,
          }}
        />
      )}
    </AppModalContainer>
  );
};

export default ViewCandidateModal;
